import { useEffect } from 'react'

import { i18n } from '@localization'
import { useRoute } from '@react-navigation/native'
import { useToast } from '@zet/common/components/toast'
import { useAddCustomerNextStateWeb } from '@zet/customer'
import useAddCustomerStore from '@zet/customer/src/store/addCustomer'
import { colors } from '@zet/design-system'
import { Icon } from '@zet/design-system/icons'
import { Input } from '@zet/design-system/input'
import { Stack, XStack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import { B1, B2, B5, H5, SH3 } from '@zet/design-system/typography'
import { useExperiments } from '@zet/experiments'
import { styled } from 'nativewind'
import { Controller, useForm } from 'react-hook-form'

import BottomView from '@components/bottomView'
import KeyBoardWrapper from '@components/keyboardWrapper'

import { useModal } from '@hooks/useModal'
import { useNavigation } from '@hooks/useNavigation'

import {
  useAddEmploymentDetails,
  useGetCriteriaIndexes,
  useGetPrefilledData,
} from '../../../../api/webflow/addCustomer/hooks'

type DetailsCardSectionProps = {
  iconName: string
  label: string
  details: string
}

const DetailsCardSection = styled(
  ({ iconName, label, details, ...props }: DetailsCardSectionProps) => {
    return (
      <XStack {...props} className=" items-center ">
        <Icon type={iconName} color={colors.grays[80]} />
        <Stack className=" ml-4/2 ">
          <B2 className=" text-grays-60 ">{label}</B2>
          <SH3 className=" text-grays-80 ">{details}</SH3>
        </Stack>
      </XStack>
    )
  },
)

const EmploymentDetailsWF = () => {
  const navigation = useNavigation()
  const { params } = useRoute()
  const {
    disableBankNameFieldWhenCash,
    panAfterVerify,
    nameAsPerPan,
    hashAfterOtp,
    customerId,
    customerName,
    customerPhoneNumber,
    isWebFlow,
  } = useAddCustomerStore()

  const { data: prefilledData } = useGetPrefilledData({
    customerId: params?.customerId || customerId,
  })

  const { data: criteriaData, refetch } = useGetCriteriaIndexes({
    locale: i18n.locale,
  })
  const { data, mutate, isSuccess, isLoading } = useAddEmploymentDetails()
  const { data: experiments } = useExperiments()
  const { mutate: mutateWithExperiments, isLoading: experimentLoading } =
    useAddCustomerNextStateWeb()

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    register,
    unregister,
    watch,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    shouldUnregister: true,
    reValidateMode: 'onChange',
  })

  const { openModal } = useModal()
  const toast = useToast()

  useEffect(() => {
    refetch()
  }, [i18n.locale])

  useEffect(() => {
    if (
      !isWebFlow &&
      experiments?.UI_isAddCustomerNewFlow &&
      params?.customerId
    ) {
      mutateWithExperiments({
        customerId: params?.customerId || customerId,
        screenName: 'GET_CURRENT_STATE',
        fullName: params?.customerName,
        phoneNumber: params?.customerPhoneNumber,
      })
    }
  }, [
    customerId,
    experiments?.UI_isAddCustomerNewFlow,
    mutateWithExperiments,
    params?.customerId,
    isWebFlow,
  ])

  useEffect(() => {
    if (isSuccess) {
      if (data?.response) {
        navigation.navigate('BrandPage', {
          customerId: params?.customerId || customerId,
          customerName: params?.customerName || customerName,
          customerPhoneNumber:
            params?.customerPhoneNumber || customerPhoneNumber,
          isBureauReportExpired: false,
          hashNew: params?.hashNew || hashAfterOtp,
          brandSpecificPartnerId: params?.brandSpecificPartnerId,
        })
      }
    }
  }, [isSuccess])

  const watchEmploymentType = watch('employmentType')

  useEffect(() => {
    if (watchEmploymentType === 'Not Working') {
      unregister('occupation')
      unregister('bankName')
    } else {
      register('occupation')
      if (!params?.disableBankName || !disableBankNameFieldWhenCash)
        register('bankName')
    }
  }, [register, unregister, watchEmploymentType])

  const onSubmit = () => {
    const occupationData = criteriaData?.find(
      (value) => value.criteriaName === 'currentOccupation',
    )
    const occupationD = occupationData?.fields?.find(
      (value) => value?.displayValue === getValues('currentOccupation'),
    )
    const employmentData = criteriaData?.find(
      (value) => value.criteriaName === 'employmentType',
    )
    const employmentD = employmentData?.fields?.find(
      (value) => value?.displayValue === getValues('employmentType'),
    )
    const urlParams = new URLSearchParams(window.location.search)
    const hash = urlParams.get('hashNew')
    const requestBodyData = {
      annualIncome: Math.trunc(getValues('annualIncome')),
      hash: hash,
      currentOccupation: Number(occupationD?.key) || null,
      employmentType: Number(employmentD?.key),
      ...(experiments?.UI_isAddCustomerNewFlow
        ? {
            screenName: 'SHOWN_EMPLOYMENT_DETAILS_SCREEN',
            bankNameList: getValues('bankName'),
            customerId: params?.customerId,
          }
        : { bankName: getValues('bankName') }),
    }
    if (experiments?.UI_isAddCustomerNewFlow) {
      mutateWithExperiments(requestBodyData)
    } else {
      mutate(requestBodyData)
    }
  }

  return (
    <KeyBoardWrapper
      className="flex-1 bg-white"
      BottomSheet={
        <BottomView
          stepCount={
            !data?.UI_isAddCustomerNewFlow
              ? 3
              : params?.screenName === 'SHOWN_BASIC_DETAILS_SCREEN'
                ? 3
                : 4
          }
          maxCount={experiments?.UI_isAddCustomerNewFlow ? 4 : 3}
          onPress={handleSubmit(onSubmit)}
          disabled={!isValid}
          loading={isLoading || experimentLoading}
          buttonEvent={{
            eventName: 'click_action',
            payload: {
              item_name: 'add_customer_step_3_continue',
              ...(params?.customerId || customerId
                ? { customer_id: params?.customerId || customerId }
                : {}),
              customer_annual_income: getValues('annualIncome'),
              customer_employment_type: getValues('employmentType'),
              customer_occupation: getValues('currentOccupation'),
              customer_bank_account: getValues('bankName'),
              ...(params?.customerPhoneNumber || customerPhoneNumber
                ? {
                    customer_phone:
                      params?.customerPhoneNumber || customerPhoneNumber,
                  }
                : {}),
            },
          }}
        />
      }
    >
      <Stack className="px-3">
        <H5>{i18n.t('webFlow.employmentDetails.details')}</H5>
        {!experiments?.UI_isAddCustomerNewFlow && (
          <Stack className=" border-grays-40 mt-3 rounded-lg border p-3  ">
            <DetailsCardSection
              iconName="UserOutlined"
              label="Customer Name as per PAN"
              details={
                prefilledData?.nameAsPan || params?.nameAsPerPan || nameAsPerPan
              }
            />
            <DetailsCardSection
              iconName="IdCard"
              label="Customer PAN Number"
              details={
                prefilledData?.panNo || params?.panNumber || panAfterVerify
              }
              className=" mt-3 "
            />
          </Stack>
        )}

        <Stack className="mt-4">
          <B2 className="text-grays-80 mb-2">
            {i18n.t('webFlow.employmentDetails.type')}
            <B1 className="text-error-100 pl-1">*</B1>
          </B2>
          <Controller
            control={control}
            rules={{
              required: {
                value: true,
                message: 'Employment type is required',
              },
            }}
            render={({ field: { onBlur, value } }) => (
              <Pressable
                onPress={() =>
                  openModal('WebFlowSelectorModal', {
                    title: i18n.t('webFlow.employmentDetails.type'),
                    iconName: 'WorkOutlined',
                    criteriaName: 'employmentType',
                    options: criteriaData,
                    setValue: setValue,
                  })
                }
              >
                <Input
                  isEditable={false}
                  placeholder={i18n.t('employmentDetails.type')}
                  onBlur={onBlur}
                  iconLeft={
                    <Icon type="WorkOutlined" color={colors.grays[80]} />
                  }
                  iconRight={
                    <Icon
                      type="ArrowDown"
                      color={colors.grays[80]}
                      className="mr-1 mt-1"
                    />
                  }
                  className="justify-center"
                  style={{
                    color: colors.grays[100],
                    fontFamily: 'Graphie-Book',
                    marginLeft: 4,
                  }}
                  value={value}
                  // showErrorText={errors.employmentType?.message}
                />
              </Pressable>
            )}
            name="employmentType"
          />
          {errors.employmentType?.message ? (
            <Stack className="-mt-3 mb-3">
              <B5 className="text-error-100">
                {errors.employmentType?.message}
              </B5>
            </Stack>
          ) : null}
        </Stack>

        {/* {watchEmploymentType !== 'Not Working' && (
          <Stack>
            <B2 className="mb-2 text-grays-80">
              {i18n.t('webFlow.employmentDetails.occupation')}
              <B1 className="pl-1 text-error-100">*</B1>
            </B2>
            <Controller
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Occupation is required',
                },
              }}
              render={({ field: { onBlur, value } }) => (
                <Pressable
                  onPress={() =>
                    openModal('WebFlowSelectorModal', {
                      title: 'Occupation',
                      iconName: 'WorkOutlined',
                      criteriaName: 'currentOccupation',
                      options: criteriaData,
                      setValue: setValue,
                    })
                  }
                >
                  <Input
                    isEditable={false}
                    placeholder="Eg: Private Employee"
                    onBlur={onBlur}
                    iconLeft={
                      <Icon type="WorkOutlined" color={colors.grays[80]} />
                    }
                    iconRight={
                      <Icon
                        type="ArrowDown"
                        color={colors.grays[80]}
                        className="mr-1 mt-1"
                      />
                    }
                    className="justify-center"
                    style={{
                      color: colors.grays[100],
                      fontFamily: 'Graphie-Book',
                      marginLeft: 4,
                    }}
                    value={value}
                    // showErrorText={errors.employmentType?.message}
                  />
                </Pressable>
              )}
              name="currentOccupation"
            />
            {errors.occupation?.message ? (
              <Stack className="-mt-3 mb-3">
                <B5 className="text-error-100">{errors.occupation?.message}</B5>
              </Stack>
            ) : null}
          </Stack>
        )} */}

        <Stack>
          <B2 className="text-grays-80 mb-1">
            {i18n.t('webFlow.employmentDetails.annual')}
            <B1 className="text-error-100 pl-1">*</B1>
          </B2>
          <Controller
            control={control}
            rules={{
              required: {
                value: true,
                message: 'Income is required',
              },
              min: {
                value: 1,
                message: 'Income is required',
              },
              minLength: 1,
              pattern: {
                value: /^(0|[1-9]\d*)(\.\d+)?$/,
                message: 'Please enter only numbers',
              },
              validate: (value) => value > 0,
            }}
            render={({ field: { onBlur, onChange, value } }) => (
              <Input
                isEditable={true}
                placeholder="Eg: ₹14,427"
                onBlur={(e) => onBlur(e)}
                className="justify-center"
                style={{
                  color: colors.grays[100],
                  fontFamily: 'Graphie-Book',
                }}
                onChangeText={(value) => {
                  onChange(value)
                }}
                keyboardType={'number-pad'}
                value={value}
                // showErrorText={errors.employmentType?.message}
              />
            )}
            name="annualIncome"
          />
          {errors.annualIncome?.message ? (
            <Stack className="mt-2">
              <B5 className="text-error-100">{errors.annualIncome?.message}</B5>
            </Stack>
          ) : null}
        </Stack>

        {watchEmploymentType !== 'Not Working' &&
          (!params?.disableBankName || !disableBankNameFieldWhenCash) && (
            <Stack className="mt-4 ">
              <B2 className="text-grays-80 mb-2">
                {i18n.t('webFlow.employmentDetails.bankName')}
                <B1 className="text-error-100 pl-1">*</B1>
              </B2>
              <Controller
                control={control}
                render={({ field: { onBlur, value } }) => (
                  <Pressable
                    onPress={() =>
                      openModal('WebFlowSelectorModal', {
                        title: i18n.t('webFlow.employmentDetails.bankName'),
                        iconName: 'BankOutlined',
                        criteriaName: 'bankName',
                        options: criteriaData,
                        setValue: setValue,
                      })
                    }
                  >
                    <Input
                      isEditable={false}
                      placeholder="Eg: SBI"
                      onBlur={onBlur}
                      iconLeft={
                        <Icon type="BankOutlined" color={colors.grays[80]} />
                      }
                      iconRight={
                        <Icon
                          type="ArrowDown"
                          color={colors.grays[80]}
                          className="-mt-2 mr-1"
                        />
                      }
                      className="justify-center"
                      style={{
                        color: colors.grays[100],
                        fontFamily: 'Graphie-Book',
                        marginLeft: 4,
                      }}
                      value={value?.toString()}
                    />
                  </Pressable>
                )}
                name="bankName"
              />
              {errors.bankName?.message ? (
                <Stack className="-mt-3 mb-3">
                  <B5 className="text-error-100">{errors.bankName?.message}</B5>
                </Stack>
              ) : null}
            </Stack>
          )}
      </Stack>
    </KeyBoardWrapper>
  )
}

export default EmploymentDetailsWF
